import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../../context/AuthContext";
import { ConfigContext } from "../../context/ConfigContext";
import ArchieButton from "./ArchieButton";
import ArchieChatWindow from "./ArchieChatWindow";
import {
  useStartArchieChat,
  useContinueArchieChat,
  useLoadArchieConversation,
  useInvalidateArchieConversations,
} from "../../hooks/useArchieChat";
import { useArchieStream } from "../../hooks/useArchieStream";
import useArchieLayout from "../../hooks/useArchieLayout";
import { ArchieChatMessage } from "../../types/archie/archie";

const SESSION_KEY = "archie_conversation_id";
const MESSAGES_KEY = "archie_messages";
const USER_KEY = "archie_user_id";

/**
 * Extracts a /customers/... search URL from assistant message text.
 * Used when loading historical messages that don't have a separate searchUrl field.
 */
const extractSearchUrl = (text: string): string | undefined => {
  const match = /\/customers\/[^\s)\]"]+/.exec(text);
  return match ? match[0] : undefined;
};

const ArchieChatWidget = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const { user } = useContext(AuthContext);
  const userId = user?.userId;
  // CK-2215: when config.json ships an ArchieUrl, chat goes to the streaming Archie endpoint.
  // Absent (every deployed config today) -> the legacy /v1/ai/chat path below runs unchanged.
  const config = useContext(ConfigContext);
  const archieUrl = config?.ArchieUrl;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ArchieChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const prevCustomerIdRef = useRef<string | undefined>(undefined);
  const prevUserIdRef = useRef<string | undefined>(undefined);

  const queryClient = useQueryClient();
  const startChat = useStartArchieChat();
  const continueChat = useContinueArchieChat();
  const loadConversation = useLoadArchieConversation();
  const invalidateConversations = useInvalidateArchieConversations();
  const { isStreaming, sendStreaming, resetSession } = useArchieStream();
  const isLoading = startChat.isPending || continueChat.isPending || isStreaming;
  const { bubblePosition, windowPosition, windowSize, handleBubbleDrag, handleResize } =
    useArchieLayout(isOpen);

  // Restore conversation from sessionStorage on mount, but only if it belongs to the current user
  useEffect(() => {
    const storedUserId = sessionStorage.getItem(USER_KEY);
    if (userId && storedUserId && storedUserId !== userId) {
      // Different user — discard stale session data
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(MESSAGES_KEY);
      sessionStorage.removeItem(USER_KEY);
      return;
    }

    const storedId = sessionStorage.getItem(SESSION_KEY);
    const storedMessages = sessionStorage.getItem(MESSAGES_KEY);

    if (storedId) {
      setConversationId(storedId);
    }
    if (storedMessages) {
      try {
        const parsed = JSON.parse(storedMessages) as ArchieChatMessage[];
        setMessages(
          parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }))
        );
      } catch {
        sessionStorage.removeItem(MESSAGES_KEY);
      }
    }
  }, [userId]);

  // Persist conversation state to sessionStorage
  useEffect(() => {
    if (conversationId) {
      sessionStorage.setItem(SESSION_KEY, conversationId);
      if (userId) {
        sessionStorage.setItem(USER_KEY, userId);
      }
    }
  }, [conversationId, userId]);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  // Clear conversation when switching to a different customer or user.
  useEffect(() => {
    const customerChanged =
      prevCustomerIdRef.current &&
      customerId &&
      prevCustomerIdRef.current !== customerId;
    const userChanged =
      prevUserIdRef.current &&
      userId &&
      prevUserIdRef.current !== userId;

    if (customerChanged || userChanged) {
      setMessages([]);
      setConversationId(null);
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(MESSAGES_KEY);
      sessionStorage.removeItem(USER_KEY);
      resetSession(); // streaming sessionId must not span tenants/users (CK-2215)
    }
    if (customerId) {
      prevCustomerIdRef.current = customerId;
    }
    if (userId) {
      prevUserIdRef.current = userId;
    }
  }, [customerId, userId, resetSession]);

  const handleSend = useCallback(
    (text: string) => {
      if (!customerId) return;

      // Add user message immediately
      const userMessage: ArchieChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        text,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // ── Streaming path (CK-2215): active only when config.json ships ArchieUrl. ──
      if (archieUrl) {
        const assistantId = crypto.randomUUID();
        // Append the assistant message on the first delta, then update it in place so the
        // answer streams into the chat live; final replaces it with the completed text.
        const upsertAssistant = (msgText: string, searchUrl?: string) => {
          setMessages((prev) =>
            prev.some((m) => m.id === assistantId)
              ? prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, text: msgText, searchUrl: searchUrl ?? m.searchUrl }
                    : m
                )
              : [
                  ...prev,
                  {
                    id: assistantId,
                    role: "assistant" as const,
                    text: msgText,
                    timestamp: new Date(),
                    searchUrl,
                  },
                ]
          );
        };

        void sendStreaming({
          archieUrl,
          message: text,
          // Prior turns only — `messages` here is the pre-send list (the user message above
          // hasn't flushed into this closure), which is exactly what the protocol expects.
          history: messages.map((m) => ({ role: m.role, content: m.text })),
          customerId,
          onDelta: (accumulated) => upsertAssistant(accumulated),
          onFinal: ({ text: finalText }) => {
            upsertAssistant(finalText, extractSearchUrl(finalText));
            // Refresh dashboard config in case Archie created a widget
            queryClient.invalidateQueries({
              queryKey: ["dashboard-config", customerId],
            });
          },
          onError: ({ message }) => {
            // Archie's error payloads are already user-facing (throttle/limit/stream guidance).
            upsertAssistant(
              message || "Sorry, something went wrong. Please try again."
            );
          },
        });
        return;
      }

      const onSuccess = (data: {
        conversationId: string;
        assistantText: string;
        searchUrl?: string;
      }) => {
        setConversationId(data.conversationId);

        const assistantMessage: ArchieChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          text: data.assistantText,
          timestamp: new Date(),
          searchUrl: data.searchUrl ?? undefined,
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Invalidate the conversations list so new conversations appear in history
        invalidateConversations(customerId);

        // Refresh dashboard config in case Archie created a widget
        queryClient.invalidateQueries({ queryKey: ["dashboard-config", customerId] });
      };

      const onError = () => {
        const errorMessage: ArchieChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          text: "Sorry, something went wrong. Please try again.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      };

      if (conversationId) {
        continueChat.mutate(
          {
            conversationId,
            request: { userText: text, customerId, conversationId },
          },
          { onSuccess, onError }
        );
      } else {
        startChat.mutate(
          { userText: text, customerId },
          { onSuccess, onError }
        );
      }
    },
    [customerId, conversationId, startChat, continueChat, invalidateConversations, queryClient, archieUrl, messages, sendStreaming]
  );

  const handleLoadConversation = useCallback(
    (selectedConversationId: string) => {
      loadConversation.mutate(selectedConversationId, {
        onSuccess: (data) => {
          const loadedMessages: ArchieChatMessage[] = data.messages.map(
            (m) => ({
              id: m.messageId,
              role: m.role as "user" | "assistant",
              text: m.text,
              timestamp: new Date(m.createdAt),
              searchUrl:
                m.role === "assistant"
                  ? extractSearchUrl(m.text)
                  : undefined,
            })
          );
          setMessages(loadedMessages);
          setConversationId(data.conversationId);
          sessionStorage.setItem(SESSION_KEY, data.conversationId);
          sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(loadedMessages));
          if (userId) {
            sessionStorage.setItem(USER_KEY, userId);
          }
        },
        onError: () => {
          const errorMessage: ArchieChatMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            text: "Sorry, I couldn't load that conversation. Please try again.",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
        },
      });
    },
    [loadConversation, userId]
  );

  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(MESSAGES_KEY);
    resetSession(); // start a fresh streaming conversation grouping too (CK-2215)
  }, [resetSession]);

  // Only render when scoped to a customer
  if (!customerId) return null;

  return (
    <>
      <ArchieButton
        bottom={bubblePosition.bottom}
        right={bubblePosition.right}
        onClick={() => setIsOpen((prev) => !prev)}
        onDrag={handleBubbleDrag}
      />
      <ArchieChatWindow
        open={isOpen}
        messages={messages}
        isLoading={isLoading}
        customerId={customerId}
        bottom={windowPosition.bottom}
        right={windowPosition.right}
        width={windowSize.width}
        height={windowSize.height}
        onSend={handleSend}
        onClose={() => setIsOpen(false)}
        onLoadConversation={handleLoadConversation}
        onNewConversation={handleNewConversation}
        onResize={handleResize}
      />
    </>
  );
};

export default ArchieChatWidget;
